'use client';

import { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { MAX_TREE_DEPTH } from '../_constants';
import { fetchMoveFileOrFolder } from '../apis';
import type { FileItem, InferV1MoveReplyFailedItem } from '../types';

export interface UseMoveFileOptions {
  onSuccess?: (successIds: string[]) => void;
  onError?: () => void;
  manual?: boolean;
}

export function useMoveFile(options: UseMoveFileOptions = {}) {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { onSuccess, onError, manual = true } = options;

  const { run: moveFiles, loading: movingFiles } = useFetch(
    (itemsToMove: FileItem[], targetFolderId: string) =>
      fetchMoveFileOrFolder({
        body: {
          items: itemsToMove.map(({ id, type }) => ({
            id,
            type,
          })),
          target_parent_id: targetFolderId,
        },
      }),
    {
      manual,
      onSuccess: ({ data, error }) => {
        const getFailedDescription = (failedItems: InferV1MoveReplyFailedItem[]) => {
          if (failedItems.length === 0) {
            return null;
          }
          const failedReasons = new Set(failedItems.map((item) => item.reason));
          if (failedReasons.size > 1) {
            return t('creations.moveDialog.partialFailedMultiReasons');
          }

          switch (failedReasons.values().next().value) {
            case 'DepthOverLimit':
              return t('creations.moveDialog.partialFailedLevelLimit', { limit: MAX_TREE_DEPTH });
            case 'SelfReference':
              return t('creations.moveDialog.failSelf');
            case 'ParentReference':
            case 'CircularReference':
              return t('creations.moveDialog.failSubfolder');
            case 'StorageNodeNotFound':
              return t('creations.moveDialog.failNoExist');
            default:
              return null;
          }
        };

        const hasFailed = (data?.failed_items?.length || 0) > 0;
        const hasSuccess = (data?.success_ids?.length || 0) > 0;
        const isAllFailed = hasFailed && !hasSuccess;
        if (error || isAllFailed) {
          const failedDescription = getFailedDescription(data?.failed_items || []);
          switch (error?.reason) {
            case 'DepthOverLimit':
              openConfirmDialog({
                title: t('creations.moveDialog.levelLimit'),
                description: t('creations.moveDialog.levelLimitDescription', { limit: MAX_TREE_DEPTH }),
                primaryButtonText: t('common.dialog.gotIt'),
              });
              break;
            default:
              openConfirmDialog({
                title: t('creations.moveDialog.failTitle'),
                description: failedDescription || error?.message,
                primaryButtonText: t('common.dialog.gotIt'),
              });
              break;
          }
          onError?.();
          return;
        }

        // 可能会有部分失败，需要提示用户
        if (hasFailed) {
          const failedDescription = getFailedDescription(data.failed_items);
          openConfirmDialog({
            title: t('creations.moveDialog.partialFailed'),
            description: failedDescription || t('creations.moveDialog.partialFailedMultiReasons'),
            primaryButtonText: t('common.dialog.gotIt'),
          });
        } else if (hasSuccess) {
          toast({
            message: t('creations.moveDialog.success'),
            type: 'success',
          });
        }

        onSuccess?.(data?.success_ids || []);
      },
    },
  );

  return {
    moveFiles,
    movingFiles,
  };
}
